import React, { useMemo } from "react";
import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import useDropdown from "../../hooks/useDropdown";
import FiltersForm from "../../components/FiltersForm";
import CorrelationChartContainer from "../../components/charts/correlationChart/CorrelationChartContainer";
import RawTable from "../../components/RawTable";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

// ----------------------------------------------------- defaults & schema
const DEFAULTS = { locationids: ["79"], p1: 28, p2: 24 };
const schema = z.object({
  locationids: z
    .preprocess(
      (v) => {
        if (Array.isArray(v)) return v; // ["79","80"]
        if (typeof v === "string") return v.split(","); // "79,80" → ["79","80"]
        return [];
      },
      z.array(z.string()) // <- keep as string
    )
    .default(DEFAULTS.locationids),
  p1: z.coerce.number().default(DEFAULTS.p1),
  p2: z.coerce.number().default(DEFAULTS.p2),
});

export default function CorrelationGraph() {
  const { params, setParams } = useQueryParams(schema);

  const locOptions = useDropdown(
    "results-simple/download-dropdown-locs-active"
  );
  const parOptions = useDropdown(
    "results-simple/list-parameters-dropdown-simple-active"
  );

  const COLUMNS = [
    { title: "Date", field: "rdate", type: "date", defaultSort: "desc" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "parameter" },
    { title: "Value", field: "rvalue_converted", type: "numeric" },
    { title: "Unit", field: "unit_name" },
  ];

  const FILTERS = useMemo(
    () => [
      {
        type: "auto",
        multi: true,
        param: "locationids",
        label: "Location",
        default: params.locationids.map(String),
        options: locOptions,
      },
      {
        type: "auto",
        param: "p1",
        label: "Independent parameter (x-axis)",
        default: String(params.p1),
        options: parOptions,
      },
      {
        type: "auto",
        param: "p2",
        label: "Dependent parameter (y-axis)",
        default: String(params.p2),
        options: parOptions,
      },
    ],
    [locOptions, parOptions, params]
  );

  return (
    <>
      <Helmet title="Correlation Graph" />

      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Correlation Graph
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This tool compares the relationship between any two water quality
            parameters measured on the same date at the same site. By analyzing
            correlations, you can explore how changes in one parameter (e.g.,{" "}
            <strong>Total Phosphorus</strong>) relate to changes in another
            (e.g., <strong>Chlorophyll-a</strong>).
          </Typography>
          <Typography variant="body1" gutterBottom>
            Use this view to investigate potential cause-effect patterns, or to
            highlight whether management targets for one parameter are likely to
            influence others.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the chart and table
            update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <FiltersForm config={FILTERS} onChange={setParams} />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Graph
          </Typography>
          <CorrelationChartContainer
            locationids={params.locationids}
            p1={params.p1}
            p2={params.p2}
            setParams={() =>
              setParams({
                ...params,
                p1: params.p2,
                p2: params.p1,
              })
            }
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Graph
          </Typography>

          <Typography variant="body1" gutterBottom>
            This scatterplot compares two parameters measured on the same day
            across selected locations. The{" "}
            <strong>independent parameter</strong> appears on the x-axis and the{" "}
            <strong>dependent parameter</strong> on the y-axis. If multiple
            measurements occur for a given parameter and date, the{" "}
            <strong>average</strong> value is used to represent that day's
            result.
          </Typography>

          <Typography variant="body1" gutterBottom>
            A dashed <strong>regression line</strong> is fit using least-squares
            linear regression. Its equation and the{" "}
            <strong>Pearson correlation coefficient (r)</strong> appear above
            the chart to quantify the relationship.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Lightly shaded bands around the regression line depict the{" "}
            <strong>95% confidence interval</strong>, showing prediction
            uncertainty. Use the <strong>Exclude Outliers</strong> option to
            omit points beyond ±2 standard deviations from the trendline when
            fitting.
          </Typography>

          <Typography variant="body1" gutterBottom>
            Points flagged as potential outliers (±2σ) remain visible but do not
            influence the regression when exclusion is enabled.
          </Typography>

          <Typography variant="body1" gutterBottom>
            You can <strong>swap the axes</strong> to treat either parameter as
            the independent variable. This can reveal asymmetries in the
            relationship or offer a more intuitive view depending on your use
            case.
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Raw Data Table
          </Typography>
          <RawTable
            endPoint="results-simple/correlation-graph"
            params={{
              ...params,
              ...{ locationids: params.locationids.join(",") },
            }}
            columns={COLUMNS}
          />
        </Section>
      </main>
    </>
  );
}
